import React from 'react';
import { Shield, Clock, Award, Users, CheckCircle, ThumbsUp } from 'lucide-react';

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Expert Protection',
      description: 'Our team of experienced IP attorneys ensures your brand receives the highest level of legal protection.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Clock,
      title: '24-Hour Processing',
      description: 'We process your trademark application swiftly within 24 hours, providing fast and efficient service.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Award,
      title: '100% Satisfaction',
      description: 'We guarantee complete satisfaction with our services and stand behind our work with full confidence.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: '24/7 customer support team ready to assist you at every step of your trademark journey.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: CheckCircle,
      title: 'Proven Track Record',
      description: 'Thousands of successful trademark registrations with a high approval rate and client satisfaction.',
      color: 'from-emerald-500 to-green-500'
    },
    {
      icon: ThumbsUp,
      title: 'Affordable Pricing',
      description: 'Competitive rates starting at just $45, making brand protection accessible to businesses of all sizes.',
      color: 'from-teal-500 to-cyan-500'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Image */}
          <div className="relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-zoom">
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800"
                alt="Why Choose Us"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 to-transparent"></div>
              
              {/* Floating Stats Cards with animation */}
              <div className="absolute bottom-8 left-8 right-8 grid grid-cols-2 gap-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl animate-scale-in">
                  <div className="text-3xl font-bold text-brand-primary mb-1">10K+</div>
                  <div className="text-sm text-gray-600">Trademarks Filed</div>
                </div>
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl animate-scale-in" style={{animationDelay: '0.2s'}}>
                  <div className="text-3xl font-bold text-brand-primary mb-1">98%</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="animate-slide-up">
            <div className="inline-block bg-emerald-100 px-4 py-2 rounded-full mb-4">
              <span className="text-emerald-700 font-semibold">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
              Your Trusted Partner for{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Brand Protection
              </span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              We understand that protecting your brand is crucial to your business success. That's why we've built a reputation for excellence, reliability, and customer satisfaction in trademark registration services.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-3 group hover:translate-x-2 transition-all duration-300"
                    style={{animationDelay: `${index * 0.1}s`}}
                  >
                    <div className={`bg-gradient-to-r ${feature.color} p-2 rounded-lg group-hover:scale-110 transition-transform animate-pulse-glow`}>
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;